import React, { useCallback, useState } from 'react';
import { FormattedMessage, injectIntl, defineMessages } from 'react-intl';
import { useSelector } from 'react-redux';
import Logger from 'js-logger';
import _debounce from 'lodash/debounce';
import { userProfileSelectors } from '@lifesize/nucleus';
import { colors } from '@lifesize/ux-assets';
import { ReactComponent as LsLogoHorizontal } from '@lifesize/ux-assets/vectors/svgLogos/lsHorizontalTM.svg';
import { Button, fade, makeStyles, TextField, Theme, Typography } from '@material-ui/core';
import lsConfig from 'utils/global-config';
import { getDesktopJoinUrl, getExtension, getName, isExtensionValid } from 'utils/guest-utils';

interface Props {
  intl: { formatMessage: Function };
  noRetry: Boolean;
  hasCheckInFailure: Boolean;
}

const messages = defineMessages({
  tryAgain: { id: 'components.retryComponent.tryAgain', defaultMessage: 'Try Again' },
  hasCheckInFailure: {
    id: 'components.retryComponent.hasCheckInFailure',
    defaultMessage: 'Please contact your system administrator.'
  },
  guestExtension: {
    id: 'components.retryComponent.guestExtension',
    defaultMessage: 'Meeting Extension or User Extension'
  },
  guestExtensionInvalid: {
    id: 'components.retryComponent.guestExtension.invalid',
    defaultMessage: 'Extension "{guestExtension}" was not found. Please enter a valid extension.'
  },
  guestJoin: { id: 'components.retryComponent.guestJoin', defaultMessage: 'Join' },
  guestJoinMeeting: { id: 'components.retryComponent.guestJoinMeeting', defaultMessage: 'Join a Meeting' },
  guestName: { id: 'components.retryComponent.guestName', defaultMessage: 'Your Name' },
  guestNameDefault: { id: 'components.retryComponent.guestNameDefault', defaultMessage: 'Guest' },
  guestNameInvalid: {
    id: 'components.retryComponent.guestNameInvalid',
    defaultMessage: 'Please enter your name. It is required.'
  }
});

const Retry = (props: Props) => {
  const { intl, noRetry = false, hasCheckInFailure = false } = props;
  const [extensionIsValid, setExtensionIsValid] = useState(false);
  const [guestExtension, setGuestExtension] = useState(getExtension());
  const [guestName, setGuestName] = useState(getName() || intl.formatMessage(messages.guestNameDefault));
  const isGuestExtInvalid = useSelector((state) => userProfileSelectors.selectGuestExtNotFound(state));
  const classes = useStyles({});

  const checkExtension = useCallback(
    _debounce(async (ext) => {
      try {
        const data = await isExtensionValid(ext);
        setExtensionIsValid(!!data.isValid);
      } catch (e) {
        setExtensionIsValid(false);
        Logger.error('Error fetching extension data', e);
      }
    }, 500),
    []
  );

  const changeGuestExtension = useCallback(
    (e) => {
      const newValue = e.target.value;
      setGuestExtension(newValue);
      if (newValue) {
        checkExtension(newValue);
      } else {
        setExtensionIsValid(false);
      }
    },
    [checkExtension]
  );

  const onJoinGuest = useCallback(() => {
    window.location.href = getDesktopJoinUrl(
      guestExtension,
      guestName || intl.formatMessage(messages.guestNameDefault)
    );
  }, [guestExtension, guestName, intl]);

  const retryMessage = useCallback(() => {
    if (hasCheckInFailure) {
      return (
        <FormattedMessage
          id="components.retryComponent.hasCheckInFailure"
          defaultMessage="Please contact your system administrator."
        />
      );
    }
    if (noRetry) {
      return (
        <FormattedMessage
          id="components.retryComponent.internet.check"
          defaultMessage="Please check your internet connection."
        />
      );
    }

    return (
      <FormattedMessage
        id="components.retryComponent.internet"
        defaultMessage="Please check your internet connection, then click {tryAgain}."
        values={{
          tryAgain: <b>{intl.formatMessage(messages.tryAgain)}</b>
        }}
      />
    );
  }, [hasCheckInFailure, noRetry, intl]);

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <LsLogoHorizontal className={classes.logo} data-testid="lsLogoHorizontal" />
      </div>
      <div className={classes.content}>
        <div className={classes.modal}>
          {
            // @ts-ignore
            isGuestExtInvalid ? (
              <>
                <Typography variant="h1" component="h2">
                  {intl.formatMessage(messages.guestJoinMeeting)}
                </Typography>
                <TextField
                  error={!extensionIsValid}
                  fullWidth
                  helperText={
                    extensionIsValid ? '' : intl.formatMessage(messages.guestExtensionInvalid, { guestExtension })
                  }
                  inputProps={{
                    'data-testid': 'guest-extension-input',
                    maxLength: 200
                  }}
                  label={intl.formatMessage(messages.guestExtension)}
                  name={'extension'}
                  onBlur={() => checkExtension(guestExtension)}
                  onChange={changeGuestExtension}
                  type="text"
                  value={guestExtension}
                  variant="outlined"
                />
                <TextField
                  error={!guestName}
                  fullWidth
                  helperText={!guestName ? intl.formatMessage(messages.guestNameInvalid) : ''}
                  inputProps={{
                    'data-testid': 'guest-name-input',
                    maxLength: 32
                  }}
                  label={intl.formatMessage(messages.guestName)}
                  name={'name'}
                  onChange={(e) => setGuestName(e.target.value.trimStart())}
                  type="text"
                  value={guestName}
                  variant="outlined"
                />
                <Button
                  className="btn"
                  color="primary"
                  disabled={!extensionIsValid || !guestName}
                  fullWidth
                  onClick={onJoinGuest}
                  size="large"
                  variant="contained"
                >
                  {intl.formatMessage(messages.guestJoin)}
                </Button>
              </>
            ) : (
              <>
                <h3>
                  <FormattedMessage id="components.retryComponent.title" defaultMessage="Unable to Connect" />
                </h3>
                <div>
                  <FormattedMessage
                    id="components.retryComponent.connect"
                    defaultMessage="We were not able to connect to the Lifesize service."
                  />
                </div>
                <div>{retryMessage()}</div>
                {!noRetry && !hasCheckInFailure && (
                  <Button
                    className="btn"
                    color="primary"
                    fullWidth
                    onClick={() => {
                      lsConfig.skipOnBeforeUnload();
                      window.location.reload(true);
                    }}
                    size="large"
                    variant="contained"
                  >
                    <FormattedMessage {...messages.tryAgain} />
                  </Button>
                )}
              </>
            )
          }
        </div>
      </div>
      <div className={classes.footer}>
        <span>
          <FormattedMessage
            id="components.retryComponent.footerMessage"
            defaultMessage="Lifesize audio, web, and video conferencing... we've got your meetings covered. Try it free at {link}."
            values={{
              link: <a href="http://www.lifesize.com">lifesize.com</a>
            }}
          />
        </span>
      </div>
    </div>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    backgroundColor: colors.lifesizeBlue,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    left: 0,
    opacity: 1,
    position: 'fixed',
    top: 0,
    width: '100%',
    zIndex: 1001
  },
  header: {
    alignItems: 'center',
    color: colors.white,
    display: 'flex',
    flex: '0 0 120px',
    justifyContent: 'center',
    width: '100%'
  },
  logo: {
    height: 48,
    width: 'auto'
  },
  content: {
    alignItems: 'center',
    display: 'flex',
    flex: '1 1 auto',
    flexDirection: 'column'
  },
  modal: {
    alignItems: 'center',
    background: colors.white,
    borderRadius: 4,
    boxShadow: `${fade(colors.black, 0.3)} 0 0 20px 0`,
    display: 'flex',
    flexDirection: 'column',
    fontSize: 20,
    fontWeight: theme.typography.fontWeightLight,
    padding: `${theme.spacing(4)}px ${theme.spacing(6)}px`,
    textAlign: 'center',
    width: 475,

    '&> *:not(:first-child)': {
      marginTop: 24
    },

    '& .btn': {
      fontSize: 20,
      height: 52,
      textTransform: 'unset'
    }
  },
  footer: {
    alignItems: 'center',
    backgroundColor: colors.squidInk,
    color: colors.concrete,
    display: 'flex',
    flex: '0 0 30px',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    fontSize: 12,
    justifyContent: 'center',
    overflow: 'hidden',
    width: '100%',

    '& a': {
      color: colors.agileGreen
    },
    '& a:active': {
      color: colors.agileGreen
    },
    '& a:hover': {
      color: colors.agileGreen
    },
    '& a:visited': {
      color: colors.agileGreen
    }
  }
}));

export default injectIntl(Retry);
